import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture, coverScale } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';
import { fieldWorkerSource, buildField, marchTets, shapeBalls, sphereBall, dropOrbit, SHAPES, ISO, MAX_BALLS } from './lib/chromeflow-field.js';

// Contrat du rig partagé : interdit. Le chrome n'a aucune couleur propre, il
// reflète son propre environnement.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'chromeflow',
  family: 'geometric',
  danceability: 0.86,
  energyLevel: 0.82,
  bpmMin: 110,
  bpmMax: 160,
  movement: 0.80,
  tonality: 0.45,
  prefers: { gridStrength: [0.5, 1.0] },
  enabled: true
};

// CHROMEFLOW : du mercure liquide en 3D au centre de l'écran (R19), qui se
// déforme, se divise en gouttes, se rejoint, éclabousse sur le kick, et
// REFLÈTE un club néon qui défile autour de lui. Techno, EDM, house,
// hyperpop. Règles R1 à R19, preset party de vdjLook, horloge de scène
// partagée (« R20 », lib/scene-clock.js).
//
// LE LIQUIDE (lib/chromeflow-field.js) : des métaballes (noyau de Wyvill à
// support fini) maillées sur le CPU, dans un Web Worker, par marching
// TETRAHEDRA sur une grille 48³ (tier 2), 32³ (tier 1), 24³ (tier 0) ; les
// normales sont le GRADIENT du champ interpolé le long des arêtes : la
// surface est lisse quelle que soit la grille (critère 4, vérifié contre une
// sphère analytique : < 4° d'erreur). Le worker reçoit les boules de l'image
// et renvoie positions + normales dans des tampons échangés (aucune
// allocation par image) ; le fil principal affiche le dernier maillage reçu
// et n'a jamais plus d'une requête en vol. Sans Worker : calcul synchrone sur
// la plus petite grille. Dans le vertex shader, des ondulations capillaires
// (trois sinus, normale perturbée par leur gradient) pilotées par bands.high.
//
// L'ENVIRONNEMENT RÉFLÉCHI, le détail décisif : une carte équirectangulaire
// 512 x 256 rendue par un shader à chaque image (statique au tier 0, tournée
// à l'échantillonnage) : bandes de néon horizontales et verticales qui
// défilent, quatre lasers qui balaient, la pochette sur un écran géant (R18 :
// elle couvre son cadre), des points stroboscopiques sur les temps. Le chrome
// n'a AUCUNE couleur propre : couleur = carte(reflect(vue, normale)) x
// Fresnel fort (0,55 de face, 1 en rasant) : ce sont les reflets qui courent
// sur les courbes quand la carte tourne (critère 2). Rendu dans une cible
// MSAA privée (contours nets, la chaîne partagée n'a pas d'anti-crénelage),
// affichée par un quad ; la mesure de confort (R10) lit cette cible.
//
// LE DÉCOR : derrière, la même carte très floue et sombre (version « floue »
// du shader, 64 x 32) ; un sol miroir noir qui reflète le chrome (le maillage
// rendu une seconde fois symétrisé sous le plan du sol, assombri et estompé ;
// pas de sol au tier 0).
//
// LE MOUVEMENT : KICK (temps, bands.low fort) - onde de choc radiale
// (gonflement puis oscillation amortie) et 2 à 6 gouttes éjectées en
// balistique (gravité, rappel vers la masse quand elles redescendent) qui se
// refondent sans saut (leur noyau se mêle au champ, leur rayon s'éteint une
// fois dedans) ; bands.mid - torsion de la masse autour de l'axe vertical ;
// bands.high - ondulations fines. FORMES : GOUTTE, ANNEAU (huit sphères en
// cercle, un tore), COLONNE, NUÉE ; une forme par scène (R20), fondu des
// boules en deux temps. MESURE : la carte tourne d'un cran, changement de
// plan de caméra une mesure sur deux (gros plan, plan large, plongée), à-coup
// vdj. PHRASE : palette suivante. MONTÉE : la masse se contracte en une sphère
// parfaite qui vibre. DROP : flash (confort), explosion en 40 gouttes qui
// orbitent deux mesures puis se refondent.
//
// Debug : ?chromeshape=drop|ring|column|swarm, ?chromeenv=1 (la carte en
// incrustation), ?chromefreeze=1 (temps et maillage figés), ?chromeprobe=1 (window.__chromeflow ;
// _maskRef.value = 1 rend le chrome en blanc plat sur noir : le masque de la sonde).

const TIER = {
  0: { N: 24, maxTris: 24000, envW: 256, envH: 128, dynamicEnv: false, floor: false, msaa: 0 },
  1: { N: 32, maxTris: 40000, envW: 512, envH: 256, dynamicEnv: true, floor: true, msaa: 4 },
  2: { N: 48, maxTris: 60000, envW: 512, envH: 256, dynamicEnv: true, floor: true, msaa: 4 }
};
const FLOOR_Y = -1.05;
const NO_BPM = 128;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const SCENE_S = 15;                 // une forme par scène (deux phrases à 128 BPM)
const SHOTS = [{ d: 2.5, h: 0.25, fov: 40 }, { d: 4.0, h: 0.9, fov: 42 }, { d: 3.2, h: 2.3, fov: 44 }];
const G = 3.2;                      // la gravité des gouttes (unités / s²)

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smooth(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// ------------------------------------------------------------------ GLSL
const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// la carte d'environnement (équirectangulaire) ; uBlur : la version floue et sombre du décor
const ENV_FRAGMENT = `
  precision highp float;
  uniform float uTime, uBeat, uStrobe, uBlur, uRotBands;
  uniform vec3 uColA, uColB, uColC;
  uniform sampler2D uCover;
  uniform float uHasCover;
  uniform vec2 uCoverScale;
  uniform vec4 uLasers;            // les longitudes des quatre lasers
  varying vec2 vUv;
  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  float band(float x, float w) { return 1.0 - smoothstep(0.0, w, abs(x)); }
  void main() {
    float lon = vUv.x, lat = vUv.y;
    float soft = 1.0 + 6.0 * uBlur, dim = mix(1.0, 0.35, uBlur);
    // LES NAPPES : un club a de grandes surfaces lumineuses, pas seulement des filets - sans
    // elles le chrome, qui ne fait que refléter, était noir sur le sol noir
    vec3 ceil = mix(uColB, uColA, 0.5 + 0.5 * sin(lon * 6.2831853 * 2.0 + uTime * 0.2));
    vec3 col = ceil * 0.30 * smoothstep(0.45, 1.0, lat) + uColA * 0.06 * (1.0 - smoothstep(0.0, 0.5, lat)) + vec3(0.012, 0.012, 0.024);
    // six panneaux LED souples, entre les latitudes 0,3 et 0,7, qui respirent
    float pan = fract(lon * 6.0 + uTime * 0.01), pm = smoothstep(0.08, 0.3, pan) * (1.0 - smoothstep(0.7, 0.92, pan));
    float pid = floor(lon * 6.0 + uTime * 0.01);
    vec3 pcol = mod(pid, 3.0) < 0.5 ? uColA : (mod(pid, 3.0) < 1.5 ? uColB : uColC);
    col += pcol * 0.35 * pm * smoothstep(0.28, 0.36, lat) * (1.0 - smoothstep(0.64, 0.72, lat)) * (0.7 + 0.3 * sin(uTime * 1.3 + pid * 2.1));
    // les bandes horizontales : trois néons épais (coeur fin + halo), qui défilent en longitude
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      float l0 = 0.30 + 0.17 * fi;
      float run = 0.55 + 0.45 * sin((lon + uRotBands * (0.6 + 0.3 * fi)) * 6.2831853 * (4.0 + fi) + uTime * (0.9 + 0.4 * fi));
      vec3 c = i == 0 ? uColA : (i == 1 ? uColB : uColC);
      col += c * (band(lat - l0, 0.012 * soft) * 1.8 + band(lat - l0, 0.05 * soft) * 0.35) * run * dim;
    }
    // les bandes verticales : dix, entre les latitudes 0,22 et 0,78
    float vmask = smoothstep(0.20, 0.26, lat) * (1.0 - smoothstep(0.74, 0.80, lat));
    float lv = fract((lon + uTime * 0.012) * 10.0) - 0.5;
    col += mix(uColB, uColC, step(0.5, fract(floor((lon + uTime * 0.012) * 10.0) * 0.5))) * (band(lv / 10.0, 0.005 * soft) * 1.4 + band(lv / 10.0, 0.02 * soft) * 0.3) * vmask * dim;
    // les lasers : quatre faisceaux fins qui balaient, obliques
    for (int i = 0; i < 4; i++) {
      float fi = float(i);
      float c0 = i == 0 ? uLasers.x : (i == 1 ? uLasers.y : (i == 2 ? uLasers.z : uLasers.w));
      float slope = 0.35 * (fi < 2.0 ? 1.0 : -1.0);
      float dl = fract(lon - c0 + 0.5) - 0.5;
      float line = band(dl - slope * (lat - 0.5), 0.0025 * soft) * smoothstep(0.05, 0.3, lat) * (1.0 - smoothstep(0.7, 0.95, lat));
      col += mix(uColC, vec3(1.0), 0.4) * line * 2.4 * dim;
    }
    // l'écran géant : la pochette (R18 : elle couvre son cadre), avec son liseré
    vec2 sc = vec2((fract(lon) - 0.5) / 0.16, (lat - 0.5) / 0.24);
    if (abs(sc.x) < 1.0 && abs(sc.y) < 1.0) {
      vec2 cuv = (sc * 0.5) * uCoverScale + 0.5;
      vec3 cov = uHasCover > 0.5 ? texture2D(uCover, vec2(cuv.x, cuv.y)).rgb : mix(uColA, uColB, 0.5 + 0.5 * sc.y) * 0.6;
      col = mix(col, cov * 1.3 * dim, 1.0 - uBlur * 0.5);
    }
    float rim = band(max(abs(sc.x), abs(sc.y)) - 1.0, 0.05 * soft) * step(0.92, max(abs(sc.x), abs(sc.y)));
    col += uColA * rim * 1.4 * dim;
    // les points stroboscopiques, sur le temps
    vec2 cell = floor(vUv * vec2(24.0, 12.0));
    float hs = hash(cell + 7.0);
    vec2 pc = (cell + 0.5 + (vec2(hash(cell), hash(cell + 3.0)) - 0.5) * 0.6) / vec2(24.0, 12.0);
    float dpt = length((vUv - pc) * vec2(2.0, 1.0));
    col += vec3(1.0, 0.95, 0.9) * step(0.72, hs) * uStrobe * (1.0 - smoothstep(0.0, 0.012 * soft, dpt)) * 2.5 * dim;
    gl_FragColor = vec4(col, 1.0);
  }
`;
// le chrome : aucune couleur propre, la carte dans la direction réfléchie, Fresnel fort
const CHROME_VERTEX = `
  uniform float uTime, uRipple;
  uniform float uMirror;
  varying vec3 vN;
  varying vec3 vW;
  float rip(vec3 p) { return sin(p.x * 8.0 + uTime * 4.1) * sin(p.y * 7.0 - uTime * 3.3) + 0.6 * sin((p.z + p.x) * 11.0 + uTime * 5.7) + 0.4 * sin((p.y - p.z) * 15.0 - uTime * 2.9); }
  void main() {
    vec3 n = normalize(normal);
    vec3 p = position;
    // les ondulations capillaires : déplacement le long de la normale, normale corrigée par le gradient
    float a = uRipple;
    float h0 = rip(p), e = 0.01;
    vec3 g = vec3(rip(p + vec3(e, 0.0, 0.0)) - h0, rip(p + vec3(0.0, e, 0.0)) - h0, rip(p + vec3(0.0, 0.0, e)) - h0) / e;
    p += n * (a * h0);
    vec3 gt = g - n * dot(g, n);
    n = normalize(n - a * gt);
    vec4 w = modelMatrix * vec4(p, 1.0);
    vW = w.xyz;
    vN = normalize(mat3(modelMatrix) * n);
    gl_Position = projectionMatrix * viewMatrix * w;
  }
`;
const CHROME_FRAGMENT = `
  precision highp float;
  uniform sampler2D uEnv;
  uniform float uEnvRot, uGain, uMirror, uFloorY, uMask;
  varying vec3 vN;
  varying vec3 vW;
  vec3 env(vec3 d) { vec2 uv = vec2(atan(d.z, d.x) / 6.2831853 + 0.5 + uEnvRot, asin(clamp(d.y, -1.0, 1.0)) / 3.1415927 + 0.5); return texture2D(uEnv, uv).rgb; }
  void main() {
    vec3 N = normalize(vN);
    vec3 V = normalize(cameraPosition - vW);
    if (dot(N, V) < 0.0) N = -N;                    // (les tétraèdres n'ont pas d'enroulement garanti)
    vec3 R = reflect(-V, N);
    float cosT = max(dot(N, V), 0.0);
    // Fresnel fort : 0,45 de face, 1 en rasant ; la couleur est toute dans la carte
    float F = 0.45 + 0.55 * pow(1.0 - cosT, 3.0);
    // (x 1,7 : la carte moyenne ~0,3, sans ce gain les reflets des néons ne passaient jamais le
    // seuil de bloom ; le confort règle la moyenne)
    vec3 col = env(R) * F * 1.7;
    // la peau du métal : un rien plus sombre au centre, un rien plus froid
    col *= mix(0.85, 1.0, 1.0 - cosT) * vec3(0.96, 0.98, 1.0);
    if (uMirror > 0.5) {
      // le reflet dans le sol : plus sombre, et il s'efface avec la profondeur sous le sol
      float depth = clamp((uFloorY - vW.y) / 1.6, 0.0, 1.0);
      col *= 0.55 * (1.0 - 0.8 * depth);
    }
    if (uMask > 2.5) { gl_FragColor = vec4(vec3(F), 1.0); return; }                  // (debug : le Fresnel seul)
    if (uMask > 1.5) { gl_FragColor = vec4(normalize(vN) * 0.5 + 0.5, 1.0); return; }   // (debug : les normales)
    if (uMask > 0.5) { gl_FragColor = vec4(uMirror > 0.5 ? 0.0 : 1.0); return; }
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;
// le décor : la carte floue dans la direction du regard ; le sol : un noir glacé qui reflète la carte
const BG_FRAGMENT = `
  precision highp float;
  uniform sampler2D uEnv;
  uniform float uEnvRot, uGain;
  uniform vec3 uFwd, uRight, uUp;
  uniform vec2 uTan;
  varying vec2 vUv;
  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec3 d = normalize(uFwd + ndc.x * uTan.x * uRight + ndc.y * uTan.y * uUp);
    vec2 uv = vec2(atan(d.z, d.x) / 6.2831853 + 0.5 + uEnvRot, asin(clamp(d.y, -1.0, 1.0)) / 3.1415927 + 0.5);
    vec3 c = texture2D(uEnv, uv).rgb * 0.22;
    gl_FragColor = vec4(c * uGain, 1.0);
  }
`;
const FLOOR_VERTEX = `
  varying vec3 vW;
  void main() { vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }
`;
const FLOOR_FRAGMENT = `
  precision highp float;
  uniform sampler2D uEnv, uEnvBlur;
  uniform float uEnvRot, uGain;
  varying vec3 vW;
  vec2 eq(vec3 d) { return vec2(atan(d.z, d.x) / 6.2831853 + 0.5 + uEnvRot, asin(clamp(d.y, -1.0, 1.0)) / 3.1415927 + 0.5); }
  void main() {
    vec3 V = normalize(cameraPosition - vW);
    vec3 R = reflect(-V, vec3(0.0, 1.0, 0.0));
    float F = 0.08 + 0.5 * pow(1.0 - max(V.y, 0.0), 5.0);
    // au loin le sol se fond dans le décor flou (son bord tracait un horizon dur)
    float fade = 1.0 - smoothstep(1.5, 4.5, length(vW.xz));
    vec3 bg = texture2D(uEnvBlur, eq(-V)).rgb * 0.22;
    gl_FragColor = vec4(mix(bg, texture2D(uEnv, eq(R)).rgb * F, fade) * uGain, 1.0);
  }
`;
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex, uEnvTex;
  uniform float uShowEnv;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uTex, vUv).rgb;
    if (uShowEnv > 0.5 && vUv.x < 0.4 && vUv.y < 0.2) c = texture2D(uEnvTex, vUv / vec2(0.4, 0.2)).rgb;
    gl_FragColor = vec4(c, 1.0);
  }
`;
const LUM_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { vec3 c = texture2D(uTex, vUv).rgb; gl_FragColor = vec4(vec3(pow(dot(c, vec3(0.2126, 0.7152, 0.0722)), 1.0 / 2.2)), 1.0); }
`;

// =============================================================================

export class ChromeflowVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._L = TIER[this._tier] || TIER[1];
    const sh = urlParam('chromeshape');
    this._forceShape = SHAPES.includes(sh) ? sh : null;
    this._showEnv = urlParam('chromeenv') === '1';
    this._freeze = urlParam('chromefreeze') === '1';       // (sonde : temps et maillage figés après la première image)
    this._post = new VdjPost({ bloomStrength: 0.55, bloomThreshold: 0.78, grain: 0.015, vignette: 0.45, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._postValue = this._post.update(1);
    this._comfort = new VdjComfort();
    if (urlParam('chromeprobe') === '1' && typeof window !== 'undefined') window.__chromeflow = this;
  }

  get postProcessing() { return this._postValue; }

  init(scene) {
    this._sceneRef = scene;
    const L = this._L;
    this._size = new THREE.Vector2(1280, 720);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    // la carte d'environnement (et sa version floue pour le décor)
    // (demi-flottant dès le tier 1 : en 8 bits les néons de la carte, à 1,8, s'écrêtaient à 1 et le
    // chrome ne passait jamais le seuil de bloom ; le Pi rend et filtre le demi-flottant)
    const eopt = { depthBuffer: false, stencilBuffer: false, type: this._tier >= 1 ? THREE.HalfFloatType : THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, wrapS: THREE.RepeatWrapping, wrapT: THREE.ClampToEdgeWrapping, generateMipmaps: false };
    this._envRT = new THREE.WebGLRenderTarget(L.envW, L.envH, eopt);
    this._envBlurRT = new THREE.WebGLRenderTarget(64, 32, eopt);
    this._EU = {
      uTime: { value: 0 }, uBeat: { value: 0 }, uStrobe: { value: 0 }, uBlur: { value: 0 }, uRotBands: { value: 0 },
      uColA: { value: new THREE.Vector3(1, 0.2, 0.6) }, uColB: { value: new THREE.Vector3(0.2, 0.6, 1) }, uColC: { value: new THREE.Vector3(0.6, 1, 0.3) },
      uCover: { value: null }, uHasCover: { value: 0 }, uCoverScale: { value: new THREE.Vector2(1, 1) }, uLasers: { value: new THREE.Vector4() }
    };
    this._envMat = new THREE.ShaderMaterial({ uniforms: this._EU, vertexShader: QUAD_VERTEX, fragmentShader: ENV_FRAGMENT, depthTest: false, depthWrite: false });
    this._envScene = new THREE.Scene();
    const eq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._envMat); eq.frustumCulled = false; this._envScene.add(eq);
    this._envRendered = false;

    // la scène privée : décor, sol, reflet, chrome
    this._pscene = new THREE.Scene();
    this._cam = new THREE.PerspectiveCamera(42, 16 / 9, 0.05, 50);
    const envRef = { value: this._envRT.texture }, rotRef = { value: 0 }, gainRef = { value: 1 };
    this._BU = { uEnv: { value: this._envBlurRT.texture }, uEnvRot: rotRef, uGain: gainRef, uFwd: { value: new THREE.Vector3() }, uRight: { value: new THREE.Vector3() }, uUp: { value: new THREE.Vector3() }, uTan: { value: new THREE.Vector2() } };
    const bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._BU, vertexShader: QUAD_VERTEX.replace('0.0, 1.0);', '0.9999, 1.0);'), fragmentShader: BG_FRAGMENT, depthTest: false, depthWrite: false }));
    bg.frustumCulled = false; bg.renderOrder = 0; this._pscene.add(bg);
    if (L.floor) {
      this._FU = { uEnv: envRef, uEnvBlur: { value: this._envBlurRT.texture }, uEnvRot: rotRef, uGain: gainRef };
      const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.ShaderMaterial({ uniforms: this._FU, vertexShader: FLOOR_VERTEX, fragmentShader: FLOOR_FRAGMENT, depthTest: true, depthWrite: false }));
      floor.rotation.x = -Math.PI / 2; floor.position.y = FLOOR_Y; floor.renderOrder = 1; this._pscene.add(floor); this._floor = floor;
    }
    // le maillage du chrome : tampons à la taille maximale, zone dessinée variable
    const g = new THREE.BufferGeometry();
    this._bufA = { pos: new Float32Array(L.maxTris * 9), nrm: new Float32Array(L.maxTris * 9) };
    this._bufB = { pos: new Float32Array(L.maxTris * 9), nrm: new Float32Array(L.maxTris * 9) };
    this._posAttr = new THREE.BufferAttribute(this._bufA.pos, 3); this._posAttr.setUsage(THREE.DynamicDrawUsage);
    this._nrmAttr = new THREE.BufferAttribute(this._bufA.nrm, 3); this._nrmAttr.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', this._posAttr); g.setAttribute('normal', this._nrmAttr);
    g.setDrawRange(0, 0); g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 10);
    this._geo = g;
    this._maskRef = { value: 0 };
    const chromeU = () => ({ uEnv: envRef, uEnvRot: rotRef, uGain: gainRef, uTime: { value: 0 }, uRipple: { value: 0 }, uMirror: { value: 0 }, uFloorY: { value: FLOOR_Y }, uMask: this._maskRef });
    this._CU = chromeU();
    this._chrome = new THREE.Mesh(g, new THREE.ShaderMaterial({ uniforms: this._CU, vertexShader: CHROME_VERTEX, fragmentShader: CHROME_FRAGMENT, side: THREE.DoubleSide }));
    this._chrome.frustumCulled = false; this._chrome.renderOrder = 3; this._pscene.add(this._chrome);
    if (L.floor) {
      this._MU = chromeU(); this._MU.uMirror.value = 1;
      this._mirror = new THREE.Mesh(g, new THREE.ShaderMaterial({ uniforms: this._MU, vertexShader: CHROME_VERTEX, fragmentShader: CHROME_FRAGMENT, side: THREE.DoubleSide }));
      this._mirror.frustumCulled = false; this._mirror.renderOrder = 2;
      this._mirror.position.y = 2 * FLOOR_Y; this._mirror.scale.y = -1;
      this._pscene.add(this._mirror);
    }
    // la cible MSAA privée, le quad d'affichage, la mesure de confort
    this._target = null;
    this._dispU = { uTex: { value: null }, uEnvTex: { value: this._envRT.texture }, uShowEnv: { value: this._showEnv ? 1 : 0 } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: null } };
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: QUAD_VERTEX, fragmentShader: LUM_FRAGMENT, depthTest: false, depthWrite: false }));
    lq.frustumCulled = false; this._lumScene.add(lq);
    this._lumAge = 0; this._lumMeasured = 0.2; this._lumPending = false; this._lumAvg = 0.2; this._gain = 1; this._gainRendered = 1;

    // le worker
    this._worker = null; this._inFlight = false; this._free = this._bufB; this._syncN = 20;
    if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined' && typeof URL !== 'undefined') {
      try {
        const url = URL.createObjectURL(new Blob([fieldWorkerSource()], { type: 'text/javascript' }));
        this._worker = new Worker(url);
        this._worker.onmessage = (ev) => this._onMesh(ev.data);
        this._worker.onerror = () => { this._worker = null; this._inFlight = false; if (!this._free) this._free = { pos: new Float32Array(this._L.maxTris * 9), nrm: new Float32Array(this._L.maxTris * 9) }; };
      } catch (e) { this._worker = null; }
    }
    this._ballsBuf = new Float32Array(MAX_BALLS * 4);
    this._reqId = 0; this._meshCount = 0; this._meshMs = 0; this._meshes = 0;

    // l'état
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._paused = false;
    this._sceneClock = new SceneClock({ period: SCENE_S, start: 0 });
    this._shapeIdx = 0; this._shape = this._forceShape || SHAPES[0]; this._prevShape = this._shape; this._shapeAt = -99;
    this._low = 0.4; this._mid = 0.4; this._high = 0.3; this._build = 0;
    this._kickAt = -99; this._kickAmp = 0; this._drops = []; this._droplets = [];
    this._dropAt = null; this._dropBurst = null;
    this._shot = 0; this._shotAt = -99; this._orbit = 0; this._envRot = 0; this._envRotTarget = 0;
    this._palIdx = 0; this._hues = [0.9, 0.55]; this._paletteSeen = undefined; this._coverUrl = undefined; this._coverTex = null; this._coverAspect = 1;
    this._marksRef = undefined; this._dropMarks = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._rng = 12345;
    this._stats = { beats: 0, bars: 0, phrases: 0, kicks: 0, droplets: 0, drops: 0, meshes: 0, shots: 0 };
    this._applyPalette(null);
  }

  _rand() { this._rng = (this._rng * 1664525 + 1013904223) >>> 0; return this._rng / 4294967296; }

  // ------------------------------------------------------------ palette, pochette
  _applyPalette(pal) {
    if (pal === this._paletteSeen && pal !== null) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bd8ff];
    const hsl = { h: 0, s: 0, l: 0 };
    const h1 = vdjColor(v[0]).getHSL(hsl).h;
    let h2 = vdjColor(v[1 % v.length]).getHSL(hsl).h;
    let dh = ((h2 - h1) % 1 + 1) % 1; if (dh > 0.5) dh -= 1;
    if (Math.abs(dh) < 0.25) h2 = h1 + (dh >= 0 ? 0.33 : -0.33);     // R4
    this._hues = [h1, ((h2 % 1) + 1) % 1];
    this._setPalette(this._palIdx);
  }
  _setPalette(k) {
    const [h1, h2] = this._hues, c = new THREE.Color();
    const sets = [[h1, h2, h1 + 0.5], [h2, h1, h2 + 0.5], [h1 + 0.08, h2 + 0.08, h1 - 0.25], [h2 - 0.08, h1 + 0.5, h2 + 0.25]];
    const [a, b, d] = sets[k % sets.length];
    const put = (u, h) => { c.setHSL(((h % 1) + 1) % 1, 0.95, 0.55); u.set(c.r, c.g, c.b); };
    put(this._EU.uColA.value, a); put(this._EU.uColB.value, b); put(this._EU.uColC.value, d);
    this._envRendered = false;
  }
  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._EU.uHasCover.value = 0; this._envRendered = false;
    if (!profile.artworkUrl) return;
    const url = profile.artworkUrl;
    loadArtworkTexture(url, { mipmaps: true }, (tex) => {
      if (!this._sceneRef || url !== this._coverUrl) { tex.dispose(); return; }
      this._coverTex = tex; this._EU.uCover.value = tex; this._EU.uHasCover.value = 1;
      const img = tex.image; this._coverAspect = img && img.width ? img.width / img.height : 1;
      // l'écran géant fait 0,26 x 0,38 de la carte, soit 2,0 : 1,5... en angle : 94° x 68° -> cadre 1,37 : 1
      this._EU.uCoverScale.value.copy(coverScale(1.33, this._coverAspect));
      this._envRendered = false;
    }, () => {});
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._dropMarks = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._dropMarks.push(m.atMs);
      this._dropMarks.sort((a, b) => a - b); this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0; while (this._dropIdx < this._dropMarks.length && this._dropMarks[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._dropMarks.length && this._dropMarks[this._dropIdx] <= now) { if (now - this._dropMarks[this._dropIdx] < DROP_STALE_MS) fired = true; this._dropIdx++; }
    return fired;
  }

  // ------------------------------------------------------------ la boucle
  update(deltaTime, profile) {
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    this._applyPalette(profile.artworkPalette);
    this._loadCover(profile);
    if (this._paused) return;                     // pause : gel (le dernier maillage reste)
    const dt = this._freeze && this._meshes > 0 ? 0 : Math.min(Math.max(deltaTime, 0), 0.1);
    this._time += dt;
    this._sceneClock.update(dt);

    // l'horloge en temps, recalée sur la grille
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) { if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++; this._phaseBar = barIn; target = 4 * (this._barCount + barIn); }
      else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    const clock = this._clock, beatS = 60 / this._bpm;
    const beat = Math.floor(clock) !== Math.floor(this._clockPrev);
    const bar = Math.floor(clock / 4) !== Math.floor(this._clockPrev / 4);
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);
    const barPhase = clock / 4 - Math.floor(clock / 4), barIdx = Math.floor(clock / 4);

    // les bandes (synthétiques sans analyse)
    const director = profile.director, bands = director && director.bands;
    const real = bands && (bands.low > 0 || bands.mid > 0 || bands.high > 0);
    const tgt = real ? [clamp01(bands.low), clamp01(bands.mid), clamp01(bands.high)] : [0.5 + 0.4 * Math.exp(-(clock - Math.floor(clock)) * 6), 0.45 + 0.3 * Math.sin(clock * 0.7), 0.35 + 0.3 * Math.sin(clock * 2.3)];
    const k = 1 - Math.exp(-dt / 0.06);
    this._low += (tgt[0] - this._low) * k; this._mid += (tgt[1] - this._mid) * (1 - Math.exp(-dt / 0.25)); this._high += (tgt[2] - this._high) * k;
    // la montée
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && (director.mode === 'build' || director.mode === 'buildup')) build = Math.max(build, 0.7);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.8));

    // LE DROP : flash, explosion en 40 gouttes pendant 2 mesures, puis elles se refondent
    let drop = hasGrid && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && this._dropAt == null) {
      this._dropAt = clock; this._stats.drops++;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    if (this._dropAt != null && clock - this._dropAt > 10) this._dropAt = null;     // 8 temps d'orbite + 2 de retour

    // SCÈNE : la forme suivante ; PHRASE : la palette ; MESURE : la carte tourne d'un cran, un plan sur deux ; TEMPS : le kick
    if (this._sceneClock.wrapped && !this._forceShape) { this._prevShape = this._shape; this._shapeIdx++; this._shape = SHAPES[this._shapeIdx % SHAPES.length]; this._shapeAt = clock; }
    if (phrase) { this._stats.phrases++; this._palIdx++; this._setPalette(this._palIdx); }
    if (bar) {
      this._stats.bars++;
      this._envRotTarget += 0.08 + 0.05 * this._rand();
      if (barIdx % 2 === 0) { this._shot = (this._shot + 1 + Math.floor(this._rand() * 2)) % SHOTS.length; this._shotAt = this._time; this._stats.shots++; }
    }
    if (beat) {
      this._stats.beats++;
      const strong = this._low > 0.55 || !real;
      this._kickAt = clock; this._kickAmp = 0.1 + 0.14 * this._low; this._stats.kicks++;
      if (strong && this._dropAt == null && this._build < 0.5) this._eject(2 + Math.floor(this._rand() * 5), clock);
    }
    this._envRot += (this._envRotTarget - this._envRot) * (1 - Math.exp(-dt / 0.35));
    this._envRot += dt * 0.012;                   // et une dérive lente

    // LES BOULES de cette image
    this._assembleBalls(clock, beatS, dt);
    // la caméra : le plan courant, une orbite lente, l'à-coup vdj
    this._orbit += dt * 0.18;
    const kick = hasGrid ? vdjCameraKick(barPhase, barIdx, 4) : { zoom: 1, roll: 0 };
    this._placeCamera(kick);
    // les uniforms
    const sinceBeat = (clock - Math.floor(clock)) * beatS;
    const EU = this._EU;
    EU.uTime.value = this._time; EU.uBeat.value = Math.exp(-sinceBeat / 0.12); EU.uStrobe.value = hasGrid ? Math.exp(-sinceBeat / 0.05) * (0.5 + 0.5 * this._high) : 0.2 * Math.exp(-sinceBeat / 0.08);
    EU.uRotBands.value = this._time * 0.05;
    EU.uLasers.value.set(0.5 + 0.5 * Math.sin(this._time * 0.37), 0.5 + 0.5 * Math.sin(this._time * 0.29 + 2.0), 0.5 + 0.5 * Math.sin(this._time * 0.43 + 4.0), 0.5 + 0.5 * Math.sin(this._time * 0.23 + 1.0));
    this._CU.uTime.value = this._time; this._CU.uRipple.value = 0.0015 + 0.006 * this._high + 0.006 * this._build * (0.5 + 0.5 * Math.sin(this._time * 60));   // (plus fort, les reflets s'émiettaient en paillettes)
    if (this._MU) { this._MU.uTime.value = this._time; this._MU.uRipple.value = this._CU.uRipple.value; }
    this._BU.uEnvRot.value = this._envRot;
    // R10 : le confort (gain absolu, plafond sur la cible privée avant la chaîne partagée)
    this._lumAge += dt;
    if (this._lumAge >= 0.5) { this._lumAge = 0; this._lumPending = true; }
    this._lumAvg += (this._lumMeasured - this._lumAvg) * Math.min(1, dt / 1.0);
    const gt = Math.min(1, 0.30 / Math.max(1e-3, this._lumAvg));
    this._gain += (gt - this._gain) * Math.min(1, dt / 0.5);
    this._BU.uGain.value = this._gain;
    this._postValue = this._post.update(hasGrid ? vdjBeatChroma(clock - Math.floor(clock), beatS) : 1);
  }

  // 2 à 6 gouttes éjectées depuis la masse : vers l'extérieur et le haut, en balistique
  _eject(n, clock) {
    const base = this._baseBalls || [];
    if (!base.length) return;
    for (let i = 0; i < n && this._droplets.length < 24; i++) {
      const b = base[Math.floor(this._rand() * base.length)];
      const a = this._rand() * Math.PI * 2, up = 0.6 + 0.9 * this._rand();
      const dir = [Math.cos(a), 0, Math.sin(a)];
      const speed = 1.4 + 1.2 * this._rand();
      this._droplets.push({ p: [b[0] + dir[0] * b[3] * 0.5, b[1] + 0.1, b[2] + dir[2] * b[3] * 0.5], v: [dir[0] * speed, up * speed, dir[2] * speed], R: 0.14 + 0.08 * this._rand(), r: 0, born: this._time, merging: null });
      this._stats.droplets++;
    }
  }

  _assembleBalls(clock, beatS, dt) {
    const t = this._time;
    const sinceKick = (clock - this._kickAt) * beatS;
    // l'onde de choc : gonflement puis oscillation amortie
    const pulse = 1 + this._kickAmp * Math.exp(-sinceKick / 0.28) * Math.cos(sinceKick / 0.07) * (sinceKick < 0.03 ? sinceKick / 0.03 : 1);
    // la forme (fondu en deux temps depuis la précédente), la montée (une sphère), le drop (un noyau)
    const cur = shapeBalls(this._shape, t), prev = shapeBalls(this._prevShape, t);
    const u = smooth((clock - this._shapeAt) / 2);
    const n = Math.max(cur.length, prev.length);
    const balls = [];
    for (let i = 0; i < n; i++) {
      const a = prev[i] || [prev[0][0], prev[0][1], prev[0][2], 0], b = cur[i] || [cur[0][0], cur[0][1], cur[0][2], 0];
      balls.push([lerp(a[0], b[0], u), lerp(a[1], b[1], u), lerp(a[2], b[2], u), lerp(a[3], b[3], u)]);
    }
    const sph = sphereBall(t)[0];
    const wb = smooth((this._build - 0.25) / 0.5);
    let dropW = 0;
    if (this._dropAt != null) { const d = clock - this._dropAt; dropW = d < 0.5 ? smooth(d / 0.5) : d < 8 ? 1 : 1 - smooth((d - 8) / 2); }
    for (const b of balls) {
      for (let c = 0; c < 3; c++) b[c] = lerp(b[c], sph[c] * 0, wb) * (1 - dropW * 0.6);
      b[3] = lerp(b[3], i0(balls, b) ? sph[3] : 0, wb);
      b[3] = lerp(b[3], 0.34, dropW);
      b[3] *= pulse;
    }
    function i0(arr, b) { return arr[0] === b; }
    // la torsion (bands.mid) : rotation de chaque boule autour de l'axe vertical, proportionnelle à sa hauteur
    const twist = 1.3 * this._mid * Math.sin(t * 0.8);
    for (const b of balls) { const ang = twist * b[1], c = Math.cos(ang), s = Math.sin(ang), x = b[0], z = b[2]; b[0] = x * c - z * s; b[2] = x * s + z * c; }
    this._baseBalls = balls;
    // les gouttes du drop : 40 sur des orbites, puis convergence
    const out = balls.slice();
    if (this._dropAt != null) {
      const d = clock - this._dropAt;
      const grow = smooth(d / 0.5), back = d > 8 ? smooth((d - 8) / 2) : 0;
      for (let i = 0; i < 40; i++) { const p = dropOrbit(i, d * beatS); out.push([p[0] * (1 - back), p[1] * (1 - back), p[2] * (1 - back), 0.2 * grow * (1 - back) * pulse]); }
    }
    // les gouttes éjectées : balistique, rappel vers la masse quand elles redescendent, fusion
    let maxStep = 0;
    for (const g of this._droplets) {
      const age = t - g.born;
      g.r = Math.min(g.R, g.r + dt * 1.5);
      g.v[1] -= G * dt;
      if (g.v[1] < 0 || age > 0.9) {
        // le rappel : vers la boule la plus proche
        let best = null, bd = 1e9;
        for (const b of balls) { const dd = Math.hypot(g.p[0] - b[0], g.p[1] - b[1], g.p[2] - b[2]); if (dd < bd) { bd = dd; best = b; } }
        if (best) { const kk = 6 * dt; for (let c = 0; c < 3; c++) g.v[c] += (best[c] - g.p[c]) / Math.max(bd, 0.05) * kk; }
        if (best && bd < best[3] * 0.75 && g.merging == null) g.merging = t;
      }
      const sp = Math.hypot(g.v[0], g.v[1], g.v[2]); if (sp > 4) for (let c = 0; c < 3; c++) g.v[c] *= 4 / sp;
      for (let c = 0; c < 3; c++) g.p[c] += g.v[c] * dt;
      maxStep = Math.max(maxStep, Math.hypot(g.v[0], g.v[1], g.v[2]) * dt);
      if (g.merging != null) g.r = g.R * Math.max(0, 1 - (t - g.merging) / 0.3);
      // le bord du cube : on rentre
      for (let c = 0; c < 3; c++) if (Math.abs(g.p[c]) > 0.95 - g.R) { g.p[c] = Math.sign(g.p[c]) * (0.95 - g.R); g.v[c] *= -0.3; }
      if (g.r > 0.001) out.push([g.p[0], g.p[1], g.p[2], g.r]);
    }
    this._dropletStep = maxStep;
    this._droplets = this._droplets.filter((g) => !(g.merging != null && t - g.merging > 0.3) && t - g.born < 6);
    this._balls = out.slice(0, MAX_BALLS);
  }

  _placeCamera(kick) {
    const S = SHOTS[this._shot];
    const u = smooth((this._time - this._shotAt) / 0.0001);   // la coupe est franche (party)
    const cam = this._cam;
    const a = this._orbit;
    cam.position.set(Math.cos(a) * S.d, S.h, Math.sin(a) * S.d);
    cam.fov = S.fov / kick.zoom;
    cam.aspect = this._size.x / Math.max(1, this._size.y);
    cam.near = 0.05; cam.far = 50;
    cam.up.set(0, 1, 0);
    cam.lookAt(0, -0.05, 0);                       // le centre de composition (R19)
    cam.rotateZ(kick.roll);
    cam.updateProjectionMatrix(); cam.updateMatrixWorld();
    const fwd = new THREE.Vector3(); cam.getWorldDirection(fwd);
    const right = new THREE.Vector3().crossVectors(fwd, cam.up).normalize(), up = new THREE.Vector3().crossVectors(right, fwd);
    const ty = Math.tan(THREE.MathUtils.degToRad(cam.fov) / 2);
    this._BU.uFwd.value.copy(fwd); this._BU.uRight.value.copy(right); this._BU.uUp.value.copy(up); this._BU.uTan.value.set(ty * cam.aspect, ty);
    void u;
  }

  // ------------------------------------------------------------ le maillage
  _requestMesh() {
    const balls = this._balls || [];
    const nb = Math.min(MAX_BALLS, balls.length);
    for (let i = 0; i < nb; i++) { const b = balls[i]; this._ballsBuf[4 * i] = b[0]; this._ballsBuf[4 * i + 1] = b[1]; this._ballsBuf[4 * i + 2] = b[2]; this._ballsBuf[4 * i + 3] = b[3]; }
    if (this._worker) {
      if (this._inFlight || !this._free) return;
      const f = this._free; this._free = null; this._inFlight = true;
      this._worker.postMessage({ id: ++this._reqId, N: this._L.N, balls: this._ballsBuf.slice(0, nb * 4), nb, pos: f.pos, nrm: f.nrm }, [f.pos.buffer, f.nrm.buffer]);
    } else {
      // sans worker : synchrone, sur la petite grille
      const N = this._syncN;
      if (!this._F || this._FN !== N) { this._F = new Float32Array((N + 1) ** 3); this._FN = N; }
      buildField(this._F, N, this._ballsBuf, nb);
      const f = this._free;
      const count = marchTets(this._F, N, ISO, f.pos, f.nrm, f.pos.length / 3);
      this._onMesh({ pos: f.pos, nrm: f.nrm, count, ms: 0 });
    }
  }
  _onMesh(m) {
    this._inFlight = false;
    if (!this._geo) return;
    // les tampons arrivés remplacent ceux du maillage affiché, qui redeviennent libres
    const old = { pos: this._posAttr.array, nrm: this._nrmAttr.array };
    this._posAttr.array = m.pos; this._nrmAttr.array = m.nrm;
    this._posAttr.clearUpdateRanges(); this._posAttr.addUpdateRange(0, m.count * 3); this._posAttr.needsUpdate = true;
    this._nrmAttr.clearUpdateRanges(); this._nrmAttr.addUpdateRange(0, m.count * 3); this._nrmAttr.needsUpdate = true;
    this._geo.setDrawRange(0, m.count);
    this._free = old;
    this._meshCount = m.count; this._meshMs = m.ms; this._stats.meshes++; this._meshes++;
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const w = Math.max(16, Math.round(this._size.x)), h = Math.max(16, Math.round(this._size.y));
    if (!this._target || this._target.width !== w || this._target.height !== h) {
      if (this._target) this._target.dispose();
      this._target = new THREE.WebGLRenderTarget(w, h, { samples: this._L.msaa, depthBuffer: true, stencilBuffer: false, type: this._tier >= 2 ? THREE.HalfFloatType : THREE.UnsignedByteType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
      this._dispU.uTex.value = this._target.texture; this._lumU.uTex.value = this._target.texture;
    }
    if (this._paused && this._rendered) return;
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    // le maillage de cette image (ou la demande au worker)
    if (!this._paused && !(this._freeze && this._meshes > 0)) this._requestMesh();
    // la carte d'environnement (chaque image, ou une fois au tier 0) et sa version floue
    if (this._L.dynamicEnv || !this._envRendered) {
      this._EU.uBlur.value = 0; renderer.setRenderTarget(this._envRT); renderer.render(this._envScene, this._ortho);
      this._EU.uBlur.value = 1; renderer.setRenderTarget(this._envBlurRT); renderer.render(this._envScene, this._ortho);
      this._envRendered = true;
    }
    this._gainRendered = this._gain;
    this._cam.aspect = w / h; this._cam.updateProjectionMatrix();
    renderer.setRenderTarget(this._target);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._pscene, this._cam);
    this._rendered = true;
    if (this._lumPending) {
      this._lumPending = false;
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
      let sum = 0; for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
      this._lumMeasured = sum / (256 * 255) / Math.max(0.05, this._gainRendered);
    }
    renderer.setRenderTarget(prev);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
  }

  getProbe() {
    return { tier: this._tier, N: this._L.N, worker: !!this._worker, shape: this._shape, balls: (this._balls || []).length, droplets: this._droplets.length, dropletStep: +(this._dropletStep || 0).toFixed(4),
      triangles: Math.round(this._meshCount / 3), meshMs: +this._meshMs.toFixed(1), shot: this._shot, envRot: +this._envRot.toFixed(3), build: +this._build.toFixed(2), drop: this._dropAt != null,
      lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), stats: Object.assign({}, this._stats),
      dropletList: this._droplets.map((g) => ({ p: g.p.map((x) => +x.toFixed(3)), r: +g.r.toFixed(3), merging: g.merging != null })) };
  }

  dispose() {
    if (this._worker) { this._worker.terminate(); this._worker = null; }
    if (this._sceneRef && this._display) { this._display.onBeforeRender = null; this._sceneRef.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._chrome) this._chrome.material.dispose();
    if (this._mirror) this._mirror.material.dispose();
    if (this._floor) { this._floor.geometry.dispose(); this._floor.material.dispose(); }
    if (this._geo) this._geo.dispose();
    for (const rt of [this._target, this._envRT, this._envBlurRT, this._lumTarget]) if (rt) rt.dispose();
    if (this._envMat) this._envMat.dispose();
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    if (typeof window !== 'undefined' && window.__chromeflow === this) window.__chromeflow = null;
    this._sceneRef = null; this._geo = null;
  }
}
